import React from "react";
import { ReactPhotoSphereViewer } from "react-photo-sphere-viewer";
import { ReactImageTurntable } from "react-image-turntable";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import PanoramaPhotosphereIcon from "@mui/icons-material/VrpanoTwoTone";
import CloseIcon from "@mui/icons-material/Close";

import { TVehicle } from "types";

import "./gallery.css";

import Typography from "@mui/material/Typography";

const defaultPicture = "https://cdn.carbando.eu/develop/logo512.png";

interface IGallery {
  vehicle: TVehicle;
  paperProps?: any;
  galleryProps?: any;
}

const Gallery: React.FC<IGallery> = ({
  vehicle,
  paperProps = {},
  galleryProps = {},
}): JSX.Element => {
  const [fullscreen, toggleFullscreen] = React.useState(false);
  const [fullscreenPanorama, toggleFullscreenPanorama] = React.useState(false);
  const [fullscreenPanoramaExterior, setFullscreenPanoramaExterior] =
    React.useState(true);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [swiperInstance, setSwiperInstance] = React.useState<any>(null);
  const [zoomSwiperInstance, setZoomSwiperInstance] = React.useState<any>(null);

  const zoomIn = () => {
    toggleFullscreen(true);
    zoomSwiperInstance.slideTo(
      swiperInstance.clickedIndex -
        (hasExteriorPanorama || hasInteriorPanorama ? 1 : 0),
      0,
    );
  };

  const zoomInPanorama = () => {
    toggleFullscreenPanorama(true);
  };

  const zoomOut = () => {
    swiperInstance.slideTo(currentSlide, 0);
    toggleFullscreen(false);
  };

  const zoomOutPanorama = () => {
    swiperInstance.slideTo(currentSlide, 0);
    toggleFullscreenPanorama(false);
  };

  if (
    !(
      vehicle &&
      (vehicle.picturesCount > 0 ||
        (vehicle.pictures && vehicle.pictures?.length > 0))
    )
  )
    return <EmptyGallery {...{ paperProps }} />;

  const hasExteriorPanorama: boolean = !!vehicle.app360_bilder_count;
  const hasInteriorPanorama: boolean = !!vehicle.app360_interior_picture;

  let images = Array.from(Array.from(Array(vehicle.picturesCount).keys())).map(
    (i) => `${vehicle.picturesPath}${vehicle.wgnr}_${i + 1}.jpg`,
  );

  if (vehicle.pictures && vehicle.pictures.length > 0) {
    images = vehicle.pictures.map((p) => p.src);
  }

  return (
    <>
      <Paper {...paperProps}>
        <Swiper
          onSwiper={setSwiperInstance}
          modules={[Navigation, Pagination]}
          navigation
          pagination={{ clickable: true }}
          {...galleryProps}
          onSlideChange={(event) => setCurrentSlide(event.activeIndex)}
          initialSlide={currentSlide}
        >
          {(hasExteriorPanorama || hasInteriorPanorama) && (
            <SwiperSlide
              key={`slide-interior`}
              onClick={() => zoomInPanorama()}
            >
              <Box
                sx={{
                  width: "100%",
                  backgroundColor: "grey.50",
                  "&:hover": { cursor: "zoom-in" },
                  aspectRatio: 1.333,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    backgroundImage: `url("${images[0]}")`,
                    backgroundSize: "cover",
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    opacity: 0.1,
                    filter: "blur(2px)",
                    border: 1,
                  }}
                />
                <Box
                  sx={{
                    width: { xs: 220, md: 280 },
                    height: { xs: 220, md: 280 },
                    position: "absolute",
                    backgroundColor: "grey.700",
                    borderRadius: "50%",
                    opacity: 0.7,
                  }}
                />
                <Typography
                  variant="h4"
                  component="span"
                  color="white"
                  sx={{
                    position: "absolute",
                    mb: 6,
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "1.7em",
                    lineHeight: "0.5em",
                  }}
                >
                  <PanoramaPhotosphereIcon sx={{ fontSize: "3em" }} />
                  <br />
                  360°-Ansicht
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    position: "absolute",
                    mt: 18,
                    py: 0,
                    px: 1,
                    lineHeight: 2,
                  }}
                >
                  Hier klicken
                </Button>
              </Box>
            </SwiperSlide>
          )}
          {images.map((p, i) => (
            <SwiperSlide key={`slide-${i}`} onClick={() => zoomIn()}>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  "&:hover": { cursor: "zoom-in" },
                }}
              >
                <img property="image" src={p} alt={`Bild-${i + 1}`} />
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Paper>
      <Dialog open={fullscreen} onClose={() => zoomOut()} fullScreen>
        <Swiper
          onSwiper={setZoomSwiperInstance}
          modules={[Navigation, Pagination, Keyboard]}
          navigation
          simulateTouch={false}
          pagination={{ clickable: true }}
          slidesPerView={1}
          keyboard={{
            enabled: true,
          }}
          onSlideChange={(event) => setCurrentSlide(event.activeIndex)}
          initialSlide={currentSlide}
        >
          {images.map((p, i) => (
            <SwiperSlide key={`slide-${i}`}>
              <img src={p} alt={`Bild-${i + 1}`} />
            </SwiperSlide>
          ))}
        </Swiper>
        <IconButton
          onClick={() => zoomOut()}
          aria-label="close"
          color="secondary"
          size="large"
          sx={{
            position: "absolute",
            top: "1vh",
            right: "1vw",
            zIndex: 101,
          }}
        >
          <Avatar sx={{ border: 1 }} color="primary">
            <CloseIcon />
          </Avatar>
        </IconButton>
      </Dialog>
      <Dialog
        keepMounted={false}
        open={fullscreenPanorama}
        onClose={() => zoomOutPanorama()}
        fullScreen
        maxWidth="xl"
      >
        {hasExteriorPanorama && fullscreenPanoramaExterior && (
          <Box
            sx={{
              position: "absolute",
              width: "100vw",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ReactImageTurntable
              autoRotate={{ disabled: true }}
              initialImageIndex={0}
              images={Array.from(
                Array.from(Array(vehicle.app360_bilder_count).keys()).map(
                  (i) =>
                    `${vehicle.app360_base_path}${vehicle.wgnr}_${i + 1}.jpg`,
                ),
              )}
            />
          </Box>
        )}
        {hasInteriorPanorama && !fullscreenPanoramaExterior && (
          <ReactPhotoSphereViewer
            src={vehicle.app360_interior_picture}
            height={"100%"}
            width={"100%"}
            navbar={false}
          ></ReactPhotoSphereViewer>
        )}
        {hasExteriorPanorama && hasInteriorPanorama && (
          <Box
            sx={{
              position: "absolute",
              top: "5vh",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Tabs
              sx={{
                p: 2,
                backgroundColor: "#CCCCCCAA",
                borderRadius: 1,
                border: 1,
              }}
              value={fullscreenPanoramaExterior ? "0" : "1"}
              onChange={(event, newValue) => {
                setFullscreenPanoramaExterior(newValue === "0" ? true : false);
              }}
            >
              <Tab label="Außen" value={"0"} />
              <Tab label="Innen" value={"1"} />
            </Tabs>
          </Box>
        )}
        <IconButton
          onClick={() => zoomOutPanorama()}
          aria-label="close"
          color="secondary"
          size="large"
          sx={{
            position: "absolute",
            top: "1vh",
            right: "1vw",
            zIndex: 101,
          }}
        >
          <Avatar sx={{ border: 1 }} color="primary">
            <CloseIcon />
          </Avatar>
        </IconButton>
      </Dialog>
    </>
  );
};

interface IEmptyGallery {
  paperProps: any;
}
const EmptyGallery: React.FC<IEmptyGallery> = ({ paperProps }): JSX.Element => (
  <>
    <Paper {...paperProps}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pt: 2,
        }}
      >
        <img src={defaultPicture} alt="" width="200" />
        <Typography variant="h5" sx={{ mt: 2 }} component="span">
          Bilder folgen in Kürze
        </Typography>
      </Box>
    </Paper>
  </>
);

export default Gallery;
